import React from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { MdClose, MdCheck } from 'react-icons/md';
import { findAirportCodeByCity, getAirportCity, getAirlineIata } from '../../constants/iataAirports';
import { findPortCodeByName } from '../../constants/portCodes';

const TRANSPORT_MODE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'ferry', label: '渡輪' },
  { value: 'train', label: '火車' },
  { value: 'flight', label: '航班' },
  { value: 'bus', label: '巴士' },
  { value: 'car', label: '自駕' },
  { value: 'cruise', label: '郵輪' },
  { value: 'walk', label: '步行' },
  { value: 'taxi', label: '計程車' },
  { value: 'metro', label: '地下鐵' },
  { value: 'bike', label: '單車' },
  { value: 'other', label: '其他' },
];

type Props = {
  value?: any,
  onSave?: (payload: any) => void,
  onCancel?: () => void,
  visibleFields?: string[] | undefined,
  compact?: boolean,
  saving?: boolean,
};

const TransportForm: React.FC<Props> = ({ value = {}, onSave, onCancel, visibleFields, compact, saving = false }) => {
  const initialData = value || {};
  const resolveInitialMode = () => {
    const raw = (initialData.mode || initialData.transport_type || 'ferry') as string;
    if (!raw) return 'ferry';
    const lower = String(raw).toLowerCase();
    if (lower === 'subway') return 'metro';
    if (lower === 'bicycle') return 'bike';
    if (lower === 'walking') return 'walk';
    if (lower === 'ships' || lower === 'ship') return 'cruise';
    const exists = TRANSPORT_MODE_OPTIONS.some((opt) => opt.value === lower);
    return exists ? lower : 'other';
  };
  const [mode, setMode] = React.useState(resolveInitialMode());
  const [from, setFrom] = React.useState(initialData.from || initialData.origin || '');
  const [to, setTo] = React.useState(initialData.to || initialData.destination || '');
  const [code, setCode] = React.useState(initialData.code || initialData.identifier || '');
  const [startTime, setStartTime] = React.useState(initialData.start_time || initialData.start_time_iso || '');
  const [endTime, setEndTime] = React.useState(initialData.end_time || initialData.end_time_iso || '');
  const [operator, setOperator] = React.useState(initialData.operator || initialData.carrier || '');

  const normalizeTime = (v: string | null | undefined) => {
    if (!v) return '';
    const s = String(v).trim();
    if (/^\d{1,2}:\d{2}$/.test(s)) return `${s}:00`;
    return s;
  };

  const handleSave = () => {
    const originalFrom = from || undefined;
    const originalTo = to || undefined;

    const isFlightMode = mode === 'flight';

    const resolveCode = (val?: string) => {
      if (!val) return undefined;
      if (!isFlightMode || mode === 'cruise') return undefined;
      const s = String(val).trim();
      if (/^[A-Za-z]{3}$/.test(s)) return s.toUpperCase();
      const found = findAirportCodeByCity(s);
      return found;
    };

    const fromCode = resolveCode(originalFrom);
    const toCode = resolveCode(originalTo);

    let airlineIata: string | undefined;
    if (isFlightMode) {
      const operatorTrim = operator ? String(operator).trim() : '';
      const looksLikeAirportCode = /^[A-Za-z]{3}$/.test(operatorTrim);
      airlineIata = looksLikeAirportCode ? undefined : getAirlineIata(operator || undefined);

      if (!operatorTrim && code) {
        const m = String(code).trim().match(/^([A-Za-z0-9]{2,3})\s*-?\s*\d+/);
        if (m && m[1]) {
          const inferred = m[1].toUpperCase();
          airlineIata = airlineIata || inferred;
        }
      }
    }

    const payload: any = {
      mode,
      from: fromCode || originalFrom || undefined,
      to: toCode || originalTo || undefined,
      fromLabel: isFlightMode && fromCode ? getAirportCity(fromCode) : originalFrom || undefined,
      toLabel: isFlightMode && toCode ? getAirportCity(toCode) : originalTo || undefined,
      code: code || undefined,
      operator: operator || (isFlightMode && airlineIata ? airlineIata : undefined),
      operator_iata: isFlightMode && airlineIata ? airlineIata : undefined,
      start_time: normalizeTime(startTime) || undefined,
      end_time: normalizeTime(endTime) || undefined,
      title: (isFlightMode && airlineIata ? `${airlineIata}${code ? ` ${code}` : ''}` : (code ? `${mode} ${code}` : undefined)),
      type: 'transport',
    };

    if (mode === 'cruise') {
      const portFrom = findPortCodeByName(originalFrom || from);
      const portTo = findPortCodeByName(originalTo || to);
      if (portFrom) {
        payload.port_from = portFrom.label;
        payload.port_from_code = portFrom.code;
      } else if (originalFrom) {
        payload.port_from = originalFrom;
      }
      if (portTo) {
        payload.port_to = portTo.label;
        payload.port_to_code = portTo.code;
      } else if (originalTo) {
        payload.port_to = originalTo;
      }
      payload.from = originalFrom || undefined;
      payload.to = originalTo || undefined;
      payload.fromLabel = originalFrom || undefined;
      payload.toLabel = originalTo || undefined;
    }

    if (onSave) onSave(payload);
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.topActions}>
        <TouchableOpacity onPress={() => onCancel && onCancel()} style={styles.iconBtn} accessibilityLabel="取消">
          <MdClose size={18} />
        </TouchableOpacity>
        <TouchableOpacity disabled={saving} onPress={() => { if (!saving) handleSave(); }} style={[styles.iconBtn, styles.saveIcon, saving ? styles.disabledSave : null]} accessibilityLabel="保存">
          <MdCheck size={18} color="#fff" />
        </TouchableOpacity>
      </View>

      <Text style={styles.heading}>交通</Text>

      <Text style={styles.label}>交通方式</Text>
      <View style={styles.modeRow}>
        {TRANSPORT_MODE_OPTIONS.map((option) => (
          <TouchableOpacity
            key={option.value}
            onPress={() => setMode(option.value)}
            style={[styles.modeBtn, mode === option.value ? styles.modeBtnActive : null]}
            activeOpacity={0.8}
          >
            <Text style={[styles.modeText, mode === option.value ? styles.modeTextActive : null]}>{option.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <TextInput style={styles.input} placeholder="出發地" value={from} onChangeText={setFrom} />
      <TextInput style={styles.input} placeholder="目的地" value={to} onChangeText={setTo} />
      <TextInput style={styles.input} placeholder="班次／航班號" value={code} onChangeText={setCode} />
      <TextInput style={styles.input} placeholder="營運商" value={operator} onChangeText={setOperator} />
      <TextInput style={styles.input} placeholder="出發時間（ISO 或 HH:mm 或 HH:mm:ss）" value={startTime} onChangeText={setStartTime} />
      <TextInput style={styles.input} placeholder="抵達時間（ISO 或 HH:mm 或 HH:mm:ss）" value={endTime} onChangeText={setEndTime} />
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { padding: 12 },
  heading: { fontSize: 18, fontWeight: '700', marginBottom: 12 },
  label: { fontSize: 13, color: '#374151', marginTop: 8, marginBottom: 6 },
  modeRow: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 },
  modeBtn: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1, borderColor: '#E5E7EB', marginRight: 8, marginBottom: 8 },
  modeBtnActive: { backgroundColor: '#0ea5ff', borderColor: '#0ea5ff' },
  modeText: { color: '#111827', fontSize: 14 },
  modeTextActive: { color: '#fff' },
  input: { borderWidth: 1, borderColor: '#eee', padding: 8, marginBottom: 8, borderRadius: 6 },
  topActions: {
    position: 'absolute',
    right: 12,
    top: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 10,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  saveIcon: {
    backgroundColor: '#0ea5ff'
  },
  disabledSave: {
    opacity: 0.6,
  },
});

export default TransportForm;
